import { useCallback, useState } from 'react'
import {
  extractDraftSubprojectsFromApiResponse,
  mergeRubroSheetStatesFromDraftSubprojects,
  serializeRubrosSnapshot,
  type CommittedDraftSnapshot,
  type RubroRow
} from '../../../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  getProjectAutofillDraft,
  importMetricEdifySheets
} from '../../../../../../../services/projectAutofillDraftService.ts'
const METRIC_GRID_FROM_DOC_ERROR =
  'No pudimos armar la grilla desde el archivo. Podés cargar el cómputo manualmente o revisarlo de nuevo.'

const METRIC_GRID_FROM_DOC_SKIPPED =
  'No pudimos usar este cómputo para la grilla. Revisá el archivo o cargalo manualmente.'

export type UseMetricEdifySheetImportArgs = {
  draftId: string | undefined
  draftAnalyzed: boolean
  isSaving: boolean
  draftLocked: boolean
  setRubros: React.Dispatch<React.SetStateAction<RubroRow[]>>
  committedRef: React.MutableRefObject<CommittedDraftSnapshot | null>
  applyPatchResponseToValidation: (res: unknown, id: string) => Promise<void>
}

export function useMetricEdifySheetImport(args: UseMetricEdifySheetImportArgs) {
  const {
    draftId,
    draftAnalyzed,
    isSaving,
    draftLocked,
    setRubros,
    committedRef,
    applyPatchResponseToValidation
  } = args

  const [importingTempIds, setImportingTempIds] = useState<Record<string, boolean>>({})
  const [importErrorByTempId, setImportErrorByTempId] = useState<Record<string, string>>({})

  const isImporting = useCallback(
    (tempId: string) => !!importingTempIds[tempId],
    [importingTempIds]
  )

  const importMetricEdifyForRubro = useCallback(
    async (tempId: string) => {
      if (!draftId || draftLocked || isSaving || !draftAnalyzed) return
      setImportErrorByTempId((prev) => {
        const next = { ...prev }
        delete next[tempId]
        return next
      })
      setImportingTempIds((prev) => ({ ...prev, [tempId]: true }))
      try {
        const res = await importMetricEdifySheets(String(draftId), { tempIds: [tempId] })
        const item = res.results?.find((x) => x.tempId === tempId)
        if (item?.status === 'failed') {
          setImportErrorByTempId((prev) => ({
            ...prev,
            [tempId]: METRIC_GRID_FROM_DOC_ERROR
          }))
          return
        }
        if (item?.status === 'skipped') {
          setImportErrorByTempId((prev) => ({
            ...prev,
            [tempId]: METRIC_GRID_FROM_DOC_SKIPPED
          }))
          return
        }

        let subs = extractDraftSubprojectsFromApiResponse(res.draft)
        if (!subs?.length) {
          const fresh = await getProjectAutofillDraft(String(draftId))
          await applyPatchResponseToValidation(fresh, String(draftId))
          subs = extractDraftSubprojectsFromApiResponse(fresh) ?? undefined
        } else {
          await applyPatchResponseToValidation(res.draft, String(draftId))
        }

        const mergeIds = new Set([tempId])
        setRubros((prev) => {
          const merged = mergeRubroSheetStatesFromDraftSubprojects(prev, subs, mergeIds)
          const c = committedRef.current
          if (c) {
            committedRef.current = {
              ...c,
              rubrosKey: serializeRubrosSnapshot(merged),
              rubrosCount: merged.length
            }
          }
          return merged
        })
      } catch {
        setImportErrorByTempId((prev) => ({
          ...prev,
          [tempId]: METRIC_GRID_FROM_DOC_ERROR
        }))
      } finally {
        setImportingTempIds((prev) => {
          const next = { ...prev }
          delete next[tempId]
          return next
        })
      }
    },
    [
      draftId,
      draftLocked,
      isSaving,
      draftAnalyzed,
      setRubros,
      committedRef,
      applyPatchResponseToValidation
    ]
  )

  return {
    importMetricEdifyForRubro,
    isImporting,
    importErrorByTempId
  }
}
